/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {SearchType} from '@ion/core/src/types/services';
import {splitIncludedText} from '@ion/core/src/utils/StringUtil';
import {
  AssetDriverIcon
} from '../../icons/AssetDriverIcon';
import {
  AssetTrailerIcon
} from '../../icons/AssetTrailerIcon';
import {
  AssetVehicleIcon
} from '../../icons/AssetVehicleIcon';
import {
  GeofenceTypeIcon
} from '../../icons/GeofenceTypeIcon';
import {
  HierarchyIndicatorIcon
} from '../../icons/HierarchyIndicatorIcon';
import {
  MapBookmarkIcon
} from '../../icons/MapBookmarkIcon';
import {
  ScDeviceIcon
} from '../../icons/ScDeviceIcon';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import {Option} from './AutocompleteSearch';
import {renderName} from './SearchUtils';

export interface AutocompleteSearchResultType {
  option?: Option;
  searchValue?: string;
  highlightColor?: string;
  hasList?: boolean;
  nameField?: string;
  validator?: Function;
}

const useStyles = makeStyles(theme => ({
  searchResultItem : {
    listType       : 'none',
    display        : 'flex',
    justifyContent : 'flex-start',
    alignItems     : 'center',
    height         : '48px',
    width          : '100%',
    position       : 'relative'
  },
  searchResultIcon : {
    display    : 'flex',
    alignItems : 'top',
    padding    : '0 14px 0 18px'
  },
  searchResultText : {
    color      : dlsColor('grey3'),
    ...dlsAlt('typography', 'searchInput'),
    '& #match' : {
      fontWeight      : 'bold',
      backgroundColor : ({highlightColor}: {highlightColor?: string}) => highlightColor || 'transparent'
    },
    '& #associatematch' : {
      fontWeight : 'bold',
      color      : dlsColor('caBlue')
    }
  },
  boldResultText : {
    fontWeight      : 'bold',
    backgroundColor : ({highlightColor}: {highlightColor?: string}) => highlightColor || 'transparent'
  },
  highlightText : {
    fontWeight : 'bold',
    color      : dlsColor('caBlue')
  },
  associations : {
    position : 'absolute',
    left     : '30px'
  },
  hierarchyIcon : {
    display    : 'inline',
    alignItems : 'top',
    padding    : '0px 5px'
  }
}));

const iconMap = () => ({
  [SearchType.asset]    : AssetVehicleIcon,
  [SearchType.vehicle]  : AssetVehicleIcon,
  [SearchType.geofence] : GeofenceTypeIcon,
  [SearchType.driver]   : AssetDriverIcon,
  [SearchType.place]    : MapBookmarkIcon,
  [SearchType.trailer]  : AssetTrailerIcon,
  [SearchType.scdevice] : ScDeviceIcon,
  [SearchType.fcdevice] : ScDeviceIcon
});

export const defaultValidator = (option, searchValue) => {
  const {type, hasList, list} = option;
  const name = option?.name || '';
  return {
    TypeIcon       : iconMap()[type],
    resultText     : splitIncludedText(name, searchValue, false),
    resultListText : splitIncludedText(list, searchValue, false),
    name,
    hasList
  };
};

/**
 * Defining AutocompleteSearchResult component. This is responsible for displaying the auto search result
 * with the matched search term and corresponding result type icons.
 * 
 * ```typescript
 * 
 *  import {AutocompleteSearchResult} from '@dls/src/components/search';
 * 
 *  <AutocompleteSearchResult option={option}
 *     validator={validator}
 *     searchValue={searchValue} />
 * 
 * ```
 */
export const AutocompleteSearchResult = ({
  option,
  validator,
  searchValue,
  highlightColor = null
}: AutocompleteSearchResultType) => {
  const defOption       = validator?.(option) || option;
  const classes         = useStyles({highlightColor});
  const {
    name,
    TypeIcon,
    hasList,
    resultText,
    resultListText
  } = defaultValidator(defOption, searchValue);
  let linkedDevice = resultListText;
  if (option?.isFcDevice) {
    linkedDevice = {
      splitText    : [option?.item?.assetName],
      includedText : searchValue
    };
  } else if (option?.isSciDevice) {
    linkedDevice = {
      splitText    : [option?.item?.asset_name],
      includedText : searchValue
    };
  }
  let itemHasList = hasList;
  if (option?.isFcDevice) {
    itemHasList = option?.item?.assetName?.length > 0;
  } else if (option?.isSciDevice) {
    itemHasList = option?.item?.asset_name?.length > 0;
  }
  return (
    <div className={classes.searchResultItem}>
      {TypeIcon && <div className={classes.searchResultIcon}><TypeIcon size={IconSize.medium} /></div>}
      <div className={classes.searchResultText}>
        <span>{renderName(name, searchValue)}</span>
        {(itemHasList && linkedDevice) && (<div className={classes.associations}>
          <span className={classes.hierarchyIcon}><HierarchyIndicatorIcon/></span>
          {linkedDevice?.splitText.map((listText, i) => (
            <span>{renderName(listText, searchValue, true)}</span>
          ))}
        </div>)}
      </div>
    </div>
  );
};

export default AutocompleteSearchResult;
